import { useCallback, useState } from "react";

import { styled } from "@linaria/react";
import { ArrowRightIcon, CheckIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { match } from "ts-pattern";

import Accordion from "@gx/dls/accordion/Accordion";
import Beacon, { BeaconSize, BeaconVariant } from "@gx/dls/beacons/Beacon";
import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import HelpIcon from "@gx/dls/icons/HelpIcon";
import NumberInput from "@gx/dls/inputs/NumberInput";
import TextInput from "@gx/dls/inputs/TextInput";
import Switcher from "@gx/dls/switcher/Switcher";
import Ellipsis from "@gx/dls/text/Ellipsis";
import Text, { TextVariant, TextWeight } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import Tooltip, { TooltipPosition } from "@gx/dls/tooltip/Tooltip";
import Widget from "@gx/dls/widget/Widget";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import CreateSourceModalHeader from "@/pages/sources/components/create/components/CreateSourceModalHeader";
import CreateSourceModalWrapper from "@/pages/sources/components/create/components/CreateSourceModalWrapper";
import CreateSourceNameInput from "@/pages/sources/components/create/components/CreateSourceNameInput";
import { CreateSourceWebhookActionType } from "@/pages/sources/components/create/sources/webhook/actions";
import {
  DEFAULT_BATCH_INTERVAL_SECONDS,
  DEFAULT_BATCH_MAX_SIZE,
  DEFAULT_SIGNATURE_HEADER,
} from "@/pages/sources/components/create/sources/webhook/constants";
import CreateSourceWebhookProvider from "@/pages/sources/components/create/sources/webhook/CreateSourceWebhookProvider";
import { useCreateSourceWebhook } from "@/pages/sources/components/create/sources/webhook/hooks";
import { CreateSourceWebhookPhase } from "@/pages/sources/components/create/sources/webhook/types";
import WebhookQuickCurl from "@/pages/sources/components/create/sources/webhook/WebhookQuickCurl";
import WebhookStepByStep from "@/pages/sources/components/create/sources/webhook/WebhookStepByStep";
import { CreateSourceStep } from "@/pages/sources/components/create/types";
import { EditSourceDrawerTab } from "@/pages/sources/components/edit/types";

import { NOOP } from "@/utils/helpers";

const BodyWrapper = withTheme(styled.div<PropsWithTheme>`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.base};
  padding: 16px;
`);

const FooterWrapper = withTheme(styled.div<PropsWithTheme>`
  padding: 16px;
  background-color: ${({ theme }) => theme.color.background.primary};
`);

interface CreateSourceWebhookProps {
  onClose: () => void;
  onBack: () => void;
}

const CreateSourceWebhookContent = ({ onClose, onBack }: CreateSourceWebhookProps) => {
  const navigate = useNavigate();

  const handleSuccess = useCallback(
    (sourceId: string) => {
      void navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          createSource: undefined,
          createSourceType: undefined,
          sourceId,
          sourceTab: EditSourceDrawerTab.SYNCS,
        }),
      });
    },
    [navigate],
  );

  const { state, dispatch, createWebhook, handleDone, isDisabled, nameError, isCreating } =
    useCreateSourceWebhook({ onSuccess: handleSuccess });

  const [viewMode, setViewMode] = useState<"plan2" | "plan3">("plan2");

  const handleSourceNameChange = useCallback(
    (name: string) => {
      dispatch({ type: CreateSourceWebhookActionType.SET_SOURCE_NAME, payload: name });
    },
    [dispatch],
  );

  const handleSignatureHeaderChange = useCallback(
    (value: string) => {
      dispatch({ type: CreateSourceWebhookActionType.SET_SIGNATURE_HEADER, payload: value });
    },
    [dispatch],
  );

  const handleBatchIntervalChange = useCallback(
    (value: number | null) => {
      dispatch({ type: CreateSourceWebhookActionType.SET_BATCH_INTERVAL_SECONDS, payload: value });
    },
    [dispatch],
  );

  const handleBatchMaxSizeChange = useCallback(
    (value: number | null) => {
      dispatch({ type: CreateSourceWebhookActionType.SET_BATCH_MAX_SIZE, payload: value });
    },
    [dispatch],
  );

  const handleCreateWebhook = useCallback(() => {
    void createWebhook();
  }, [createWebhook]);

  const handleBack = useCallback(() => {
    onBack();
  }, [onBack]);

  const renderBody = () => {
    const isCreated =
      state.phase === CreateSourceWebhookPhase.LISTENING ||
      state.phase === CreateSourceWebhookPhase.CONFIRMED;

    return (
      <>
        {/* Name input - always shown, disabled after creation */}
        <CreateSourceNameInput
          value={state.sourceName}
          onChange={handleSourceNameChange}
          error={nameError}
          isDisabled={isCreated || isDisabled}
        />

        {/* Accordion - always visible, open before creation, closed after */}
        <Accordion
          key={isCreated ? "created" : "configure"}
          header="Advanced configuration"
          isOpenInitial={!isCreated}
        >
          <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
            <FlexWrapper alignItems={AlignItems.CENTER} gap={4}>
              <Text variant={TextVariant.SECONDARY}>Signature Header</Text>
              <Tooltip
                body="The HTTP header name containing the HMAC signature."
                position={TooltipPosition.RIGHT}
              >
                <HelpIcon />
              </Tooltip>
            </FlexWrapper>
            <TextInput
              value={state.signatureHeader}
              onChange={handleSignatureHeaderChange}
              placeholder={DEFAULT_SIGNATURE_HEADER}
              isDisabled={isCreated || isDisabled}
              fillWidth
            />
          </FlexWrapper>

          <FlexWrapper gap={16} fillWidth>
            <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
              <FlexWrapper alignItems={AlignItems.CENTER} gap={4}>
                <Text variant={TextVariant.SECONDARY}>Batch Interval (seconds)</Text>
                <Tooltip
                  body="How often to process batched webhook events."
                  position={TooltipPosition.RIGHT}
                >
                  <HelpIcon />
                </Tooltip>
              </FlexWrapper>
              <NumberInput
                value={state.batchIntervalSeconds ?? undefined}
                onChange={handleBatchIntervalChange}
                placeholder={String(DEFAULT_BATCH_INTERVAL_SECONDS)}
                isDisabled={isCreated || isDisabled}
                fillWidth
              />
            </FlexWrapper>

            <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth>
              <FlexWrapper alignItems={AlignItems.CENTER} gap={4}>
                <Text variant={TextVariant.SECONDARY}>Batch Max Size</Text>
                <Tooltip
                  body="Maximum number of events per batch."
                  position={TooltipPosition.RIGHT}
                >
                  <HelpIcon />
                </Tooltip>
              </FlexWrapper>
              <NumberInput
                value={state.batchMaxSize ?? undefined}
                onChange={handleBatchMaxSizeChange}
                placeholder={String(DEFAULT_BATCH_MAX_SIZE)}
                isDisabled={isCreated || isDisabled}
                fillWidth
              />
            </FlexWrapper>
          </FlexWrapper>
        </Accordion>

        {/* After creation: switcher + test panel + status */}
        {isCreated && (
          <>
            <FlexWrapper alignItems={AlignItems.CENTER} fillWidth>
              <Switcher
                items={[
                  { id: "plan2", label: "Quick Curl", onClick: () => setViewMode("plan2") },
                  { id: "plan3", label: "Step by Step", onClick: () => setViewMode("plan3") },
                ]}
                selectedId={viewMode}
              />
            </FlexWrapper>

            {viewMode === "plan2" && state.credentials && (
              <WebhookQuickCurl
                credentials={state.credentials}
                signatureHeader={state.signatureHeader || DEFAULT_SIGNATURE_HEADER}
              />
            )}
            {viewMode === "plan3" && state.credentials && (
              <WebhookStepByStep
                credentials={state.credentials}
                signatureHeader={state.signatureHeader || DEFAULT_SIGNATURE_HEADER}
              />
            )}

            {/* Listening phase: status */}
            {state.phase === CreateSourceWebhookPhase.LISTENING && (
              <Widget fillWidth noHover>
                <FlexWrapper
                  alignItems={AlignItems.CENTER}
                  justifyContent={JustifyContent.SPACE_BETWEEN}
                  fillWidth
                >
                  <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
                    <Beacon variant={BeaconVariant.YELLOW} size={BeaconSize.MEDIUM} isPulse />
                    <Text variant={TextVariant.YELLOW}>
                      Waiting for webhook test event
                      <Ellipsis isPulse />
                    </Text>
                  </FlexWrapper>
                  <Button
                    label="Skip"
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.MEDIUM}
                    onClick={handleDone}
                  />
                </FlexWrapper>
              </Widget>
            )}

            {/* Confirmed phase: success */}
            {state.phase === CreateSourceWebhookPhase.CONFIRMED && (
              <Widget fillWidth noHover>
                <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
                  <Beacon variant={BeaconVariant.SUCCESS} size={BeaconSize.MEDIUM} />
                  <Text weight={TextWeight.MEDIUM}>Webhook test received!</Text>
                </FlexWrapper>
              </Widget>
            )}
          </>
        )}
      </>
    );
  };

  const renderFooter = () => {
    return match(state.phase)
      .with(CreateSourceWebhookPhase.CONFIGURE, CreateSourceWebhookPhase.ERROR, () => (
        <Button
          label="Configure"
          icon={ArrowRightIcon}
          size={ButtonSize.LARGE}
          onClick={handleCreateWebhook}
          isDisabled={isDisabled}
          isIconTrailing
          fillWidth
        />
      ))
      .with(CreateSourceWebhookPhase.CREATING, () => (
        <Button
          label="Creating..."
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={isCreating}
          isDisabled
          fillWidth
        />
      ))
      .with(CreateSourceWebhookPhase.LISTENING, () => (
        <Button
          label="Apply"
          icon={CheckIcon}
          size={ButtonSize.LARGE}
          onClick={handleDone}
          isIconTrailing
          fillWidth
        />
      ))
      .with(CreateSourceWebhookPhase.CONFIRMED, () => (
        <Button
          label="Done"
          icon={CheckIcon}
          variant={ButtonVariant.SUCCESS}
          size={ButtonSize.LARGE}
          onClick={handleDone}
          isIconTrailing
          fillWidth
        />
      ))
      .exhaustive();
  };

  return (
    <CreateSourceModalWrapper step={CreateSourceStep.CONFIGURE}>
      <CreateSourceModalHeader
        sourceType={SourceType.WEBHOOK}
        onClose={onClose}
        onBack={handleBack}
      />
      <FlexItem>
        <HorizontalDivider />
      </FlexItem>

      <BodyWrapper>
        <FlexWrapper direction={FlexDirection.COLUMN} gap={16} fillWidth>
          {renderBody()}
        </FlexWrapper>
      </BodyWrapper>

      <HorizontalDivider />
      <FooterWrapper>{renderFooter()}</FooterWrapper>
    </CreateSourceModalWrapper>
  );
};

const CreateSourceWebhook = (props: CreateSourceWebhookProps) => (
  <CreateSourceWebhookProvider>
    <CreateSourceWebhookContent {...props} />
  </CreateSourceWebhookProvider>
);

export default CreateSourceWebhook;
